import { useState } from "react";
import { History, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { scratchpadChangelog, type ChangelogArea } from "@/lib/scratchpad/changelog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ── ChangelogPanel ─────────────────────────────────────────────────────────────
//
// A sidebar trigger that opens a modal dialog listing the changelog entries from
// src/lib/scratchpad/changelog.ts. The inline collapsible expansion is replaced
// by a Dialog so the entries don't take up sidebar real-estate.

// Format an ISO date string ("2026-05-24") into a friendly, human-readable form
// ("May 24, 2026"). Dependency-free — parses the parts directly so we avoid
// timezone drift from `new Date("YYYY-MM-DD")`.
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatChangelogDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  const monthName = MONTHS[Number(month) - 1];
  if (!monthName) return iso;
  return `${monthName} ${Number(day)}, ${year}`;
}

// Tab definitions. "all" is the default and shows every bullet. The remaining
// values map to ChangelogArea tags on individual changelog items.
type ChangelogTab = "all" | ChangelogArea;

const CHANGELOG_TABS: { value: ChangelogTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "agent-builder", label: "Agent Builder" },
  { value: "ontology", label: "Ontology" },
  { value: "transforms", label: "Transforms" },
  { value: "data-sources", label: "Data Sources" },
];

export function ChangelogPanel() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ChangelogTab>("all");

  // Filter entries by the selected tab. Entries with no matching items for the
  // selected area are dropped entirely so empty date groups don't render.
  const visibleEntries = scratchpadChangelog
    .map((entry) => ({
      ...entry,
      items: tab === "all" ? entry.items : entry.items.filter((item) => item.area === tab),
    }))
    .filter((entry) => entry.items.length > 0);

  return (
    <div className="border-t border-border mt-3 pt-3" data-testid="changelog-panel">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            className={cn(
              "w-full flex items-center gap-1.5 px-1 py-1 rounded-md text-xs font-medium",
              "text-muted-foreground hover:text-foreground hover:bg-white/[0.06]",
              "transition-colors select-none"
            )}
            aria-label="Open changelog"
          >
            <History className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1 text-left">Changelog</span>
          </button>
        </DialogTrigger>

        <DialogContent className="max-w-lg" data-testid="changelog-dialog">
          <DialogHeader>
            <DialogTitle>Changelog</DialogTitle>
          </DialogHeader>

          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as ChangelogTab)}
            className="mt-1"
          >
            <TabsList
              className="flex w-full flex-wrap gap-1"
              aria-label="Filter changelog by feature area"
            >
              {CHANGELOG_TABS.map(({ value, label }) => (
                <TabsTrigger key={value} value={value} className="px-3 py-1.5 text-xs">
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <ScrollArea className="max-h-[60vh] pr-3">
            {visibleEntries.length === 0 ? (
              <p className="py-6 text-xs text-muted-foreground" data-testid="changelog-empty">
                No changes in this area yet.
              </p>
            ) : (
              <ol
                className="max-w-prose divide-y divide-border/60 py-1"
                data-testid="changelog-entries"
              >
                {visibleEntries.map((entry) => (
                  <li key={entry.date} className="py-4 first:pt-0 last:pb-0">
                    {/* Date header — clear typographic anchor for the day's entries */}
                    <p className="flex items-center gap-1.5 mb-2.5 text-xs font-semibold text-foreground">
                      <CalendarDays className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground/70" />
                      <time dateTime={entry.date}>{formatChangelogDate(entry.date)}</time>
                    </p>
                    <ul className="space-y-2">
                      {entry.items.map((item, i) => (
                        <li
                          key={i}
                          className="flex gap-2.5 text-xs text-muted-foreground leading-relaxed"
                        >
                          <span className="mt-[7px] flex-shrink-0 w-1 h-1 rounded-full bg-muted-foreground/50" />
                          <span>{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
